import type { LobbyHistoryItem } from '../../api/types';

/**
 * The history endpoint has answered in several shapes over time: a bare array,
 * `{history: [...]}`, `{items: [...]}` for a list, and `{item: {...}}` for a
 * lookup by match or lobby id. All four are accepted here so the page works
 * against whichever API version is deployed.
 */
function itemsFrom(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  for (const key of ['items', 'history'] as const) {
    const list = record[key];
    if (Array.isArray(list)) return list;
  }
  if (record.item && typeof record.item === 'object') return [record.item];
  return [];
}

/**
 * Rows written before the API settled on snake_case carry Go's field names.
 * Mapping them keeps the page readable against an API that has not been
 * updated yet; it can go once every deployment is past that version.
 */
const LEGACY_KEYS: Record<string, string> = {
  LobbyID: 'lobby_id',
  BotID: 'bot_id',
  MatchID: 'match_id',
  GameMode: 'game_mode',
  ServerRegion: 'server_region',
  LeagueID: 'league_id',
  Connect: 'connect',
  LobbyName: 'lobby_name',
  State: 'state',
  CreatedAt: 'created_at',
  Notes: 'notes',
};

function normalizeItem(raw: unknown): LobbyHistoryItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const target = LEGACY_KEYS[key] ?? key;
    // A payload carrying both spellings keeps the snake_case one.
    if (target in normalized && LEGACY_KEYS[key]) continue;
    normalized[target] = value;
  }
  return normalized as LobbyHistoryItem;
}

function createdAtMs(item: LobbyHistoryItem): number {
  const parsed = Date.parse(item.created_at ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * The rows the history page renders: one flat list, newest first. The API
 * returns them oldest first, which is not what the page says it shows.
 */
export function normalizeHistory(payload: unknown): LobbyHistoryItem[] {
  const items = itemsFrom(payload)
    .map(normalizeItem)
    .filter((item): item is LobbyHistoryItem => item !== null);

  return items.sort((left, right) => createdAtMs(right) - createdAtMs(left));
}
